import React from "react";
import { useLang } from "@/contexts/LanguageContext";
import { MapPin, Phone, Mail, HandHeart, Building2, IndianRupee, Navigation, ExternalLink } from "lucide-react";

function buildMapQuery(content) {
  const lat = (content?.latitude || "").toString().trim();
  const lng = (content?.longitude || "").toString().trim();
  if (lat && lng) return `${lat},${lng}`;
  return content?.address || "Kodungaiyur, Chennai, Tamil Nadu, India";
}

export default function ContactSection({ content }) {
  const { t, lang } = useLang();
  const fontH = lang === "ta" ? "font-serif-ta" : "font-serif-en";
  const fontB = lang === "ta" ? "font-sans-ta" : "";
  const donations = lang === "ta" ? content?.donations_info_ta : content?.donations_info_en;
  const mapQuery = buildMapQuery(content);
  const embedSrc = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&hl=${lang === "ta" ? "ta" : "en"}&z=16&output=embed`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapQuery)}`;
  const openInMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`;

  return (
    <section id="contact" data-testid="contact-section" className="bg-stone-50 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <span className="text-xs uppercase tracking-[0.3em] text-amber-700">تواصل</span>
          <h2 data-testid="contact-heading" className={`${fontH} text-4xl md:text-5xl text-emerald-950 mt-2`}>
            {t("contact_heading")}
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact card */}
          <div className="mosque-card overflow-hidden flex flex-col">
            <div className="p-8 pb-5">
              <h3 className={`${fontH} text-2xl text-emerald-950 mb-5`}>{t("nav_contact")}</h3>
              <div className="space-y-4 text-stone-700">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-emerald-900 mt-1" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500">{t("contact_address")}</div>
                    <div className={fontB}>{content?.address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-emerald-900 mt-1" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500">{t("contact_phone")}</div>
                    <a href={`tel:${content?.phone}`} className="hover:text-emerald-900">{content?.phone}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-emerald-900 mt-1" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-stone-500">{t("contact_email")}</div>
                    <a href={`mailto:${content?.email}`} className="hover:text-emerald-900">{content?.email}</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Map embed */}
            <div data-testid="contact-map" className="relative mt-2 border-t border-stone-200">
              <iframe
                title="Masjid Al Noor location map"
                src={embedSrc}
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="block w-full"
              />
            </div>
            <div className="px-8 py-5 border-t border-stone-200 flex flex-col sm:flex-row gap-3">
              <a
                data-testid="get-directions-btn"
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <Navigation className="w-4 h-4" /> {t("get_directions")}
              </a>
              <a
                data-testid="open-in-maps-btn"
                href={openInMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost-emerald inline-flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" /> {t("open_in_maps")}
              </a>
            </div>
          </div>

          {/* Donations card */}
          <div className="mosque-card p-8 border-amber-200 bg-amber-50/40">
            <div className="flex items-center gap-2 text-amber-700 mb-2">
              <HandHeart className="w-5 h-5" />
              <span className="text-xs uppercase tracking-[0.25em]">{t("donations_heading")}</span>
            </div>
            <h3 className={`${fontH} text-2xl text-emerald-950`}>صدقة جارية</h3>
            <p className={`mt-3 text-stone-700 ${fontB}`}>{donations}</p>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white rounded-lg p-3 border border-amber-100">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1"><Building2 className="w-3 h-3" />{t("bank_name")}</div>
                <div className="mt-1 text-stone-800">{content?.bank_name}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-amber-100">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">{t("account_name")}</div>
                <div className="mt-1 text-stone-800 truncate">{content?.account_name}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-amber-100">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">{t("account_number")}</div>
                <div className="mt-1 text-stone-800 font-mono">{content?.account_number}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-amber-100">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">{t("ifsc")}</div>
                <div className="mt-1 text-stone-800 font-mono">{content?.ifsc}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-amber-100 col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1"><IndianRupee className="w-3 h-3" />{t("upi_id")}</div>
                <div className="mt-1 text-emerald-900 font-mono">{content?.upi_id}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
